import type { ReconnectingWebSocket, WSMessage } from '@solid-primitives/websocket';
import { makeWS } from '@solid-primitives/websocket';

export type WSReconnectOptions = {
  delay?: number;
  retries?: number;
  onRetry?: () => void;
};

/**
 * 从 @solid-primitives/websocket[https://github.com/solidjs-community/solid-primitives/blob/main/packages/websocket/src/index.ts] 库中 fork
 * @adjust 的属于fork后的调整，其余都是原生库的实现
 *
 * @param url
 * @param protocols
 * @param options
 * @returns
 */
const makeReconnectingWS = (
  /**
   * @adjust
   * 调整 url 为动态生成
   */
  url: () => string,
  protocols?: string | string[],
  options: WSReconnectOptions = {}
) => {
  let retries = options.retries || Infinity;
  let ws: ReconnectingWebSocket;
  const queue: WSMessage[] = [];
  let events: Parameters<WebSocket['addEventListener']>[] = [
    [
      'close',
      (err) => {
        console.error('===socket state===close===', err);
        retries-- > 0 &&
          setTimeout(() => {
            /**
             * @adjust
             * 新增重连回调逻辑
             */
            options.onRetry?.();
            getWS();
          }, options.delay || 3000);
      },
    ],
  ];
  const getWS = () => {
    if (ws && ws.readyState < 2) {
      /**
       * @adjust
       * 修复bug，移除事件监听，否则 ws.close() 之后，close 事件监听还会触发，造成死循环
       */
      events.forEach((args) => ws.removeEventListener(...args));
      ws.close();
    }
    ws = Object.assign(makeWS(url(), protocols, queue), {
      reconnect: getWS,
    });
    events.forEach((args) => ws.addEventListener(...args));
  };
  getWS();
  const wws: Partial<ReconnectingWebSocket> = {
    close: (...args: Parameters<WebSocket['close']>) => {
      retries = 0;
      return ws.close(...args);
    },
    addEventListener: (...args: Parameters<WebSocket['addEventListener']>) => {
      events.push(args);
      return ws.addEventListener(...args);
    },
    removeEventListener: (...args: Parameters<WebSocket['removeEventListener']>) => {
      events = events.filter((ev) => args[0] !== ev[0] || args[1] !== ev[1]);
      return ws.removeEventListener(...args);
    },
    send: (msg: WSMessage) => {
      wws.send!.before?.();
      return ws!.send(msg);
    },
  };
  for (const name in ws!)
    wws[name as keyof typeof wws] == null &&
      Object.defineProperty(wws, name, {
        enumerable: true,
        get: () =>
          typeof ws[name as keyof typeof ws] === 'function'
            ? (ws[name as keyof typeof ws] as Function).bind(ws)
            : ws[name as keyof typeof ws],
      });
  return wws as ReconnectingWebSocket;
};

export default makeReconnectingWS;
